'use client';

import * as RadixAccordion from '@radix-ui/react-accordion';
import { FiChevronDown } from 'react-icons/fi';
import { twMerge } from 'tailwind-merge';
import { VariantProps } from 'tailwind-variants';
import { v4 as uuid } from 'uuid';
import { accordionStyle } from './styles';

interface IAccordionItemContent {
  jsx: React.ReactNode;
}

export interface IAccordionItem {
  className?: string;
  content: IAccordionItemContent[];
  headerClassName?: string;
  title: string | React.ReactNode;
  value: string;
}

interface IAccordionProps extends VariantProps<typeof accordionStyle.root> {
  className?: string;
  itemContentProps?: {
    className?: string;
    rest?: Omit<RadixAccordion.AccordionContentProps, 'className'>;
  };
  itemHeaderProps?: {
    className?: string;
    rest?: Omit<RadixAccordion.AccordionHeaderProps, 'className'>;
  };
  itemProps?: {
    className?: string;
    rest?: Omit<RadixAccordion.AccordionItemProps, 'className'>;
  };
  itemTriggerProps?: {
    className?: string;
    rest?: Omit<RadixAccordion.AccordionTriggerProps, 'className'>;
  };
  items: IAccordionItem[];
  type?: 'single' | 'multiple';
}

export function Accordion({
  className,
  itemContentProps,
  itemHeaderProps,
  itemProps,
  items,
  itemTriggerProps,
  type = 'single',
}: IAccordionProps) {
  return (
    <RadixAccordion.Root
      className={accordionStyle.root({ className })}
      collapsible={type === 'single'}
      type={type}
    >
      {items.map(item => (
        <RadixAccordion.Item
          className={accordionStyle.item({ className: itemProps?.className })}
          key={uuid()}
          value={item.value}
          {...itemProps?.rest}
        >
          <RadixAccordion.Header
            className={accordionStyle.header({
              className: twMerge(
                itemHeaderProps?.className,
                item.headerClassName,
              ),
            })}
            {...itemHeaderProps?.rest}
          >
            <RadixAccordion.Trigger
              className={accordionStyle.trigger({
                className: twMerge(itemTriggerProps?.className, item.className),
              })}
              {...itemTriggerProps?.rest}
            >
              {item.title}
              <FiChevronDown
                aria-hidden
                className="ease-[cubic-bezier(0.87,_0,_0.13,_1)] transition-transform duration-300 group-data-[state=open]:rotate-180"
                size={20}
              />
            </RadixAccordion.Trigger>
          </RadixAccordion.Header>
          {item.content.map(content => (
            <RadixAccordion.Content
              className={accordionStyle.content({
                className: itemContentProps?.className,
              })}
              key={uuid()}
              {...itemContentProps?.rest}
            >
              {content.jsx}
            </RadixAccordion.Content>
          ))}
        </RadixAccordion.Item>
      ))}
    </RadixAccordion.Root>
  );
}
